import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/common/Icon/Icon';
import SectionTitle from '../../components/common/SectionTitle/SectionTitle';
import Loading from '../../components/common/Loading/Loading';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import FilterChipsBar from '../../components/common/FilterChipsBar/FilterChipsBar';
import type { FilterChipEntry } from '../../components/common/FilterChipsBar/FilterChipsBar';
import {
  compactFilterChips,
} from '../../components/common/FilterChipsBar/buildFilterChips';
import Badge from '../../components/common/Badge/Badge';
import Button from '../../components/common/Button/Button';
import ProjectBadge from '../../components/common/ProjectBadge/ProjectBadge';
import ReactivatedBadge from '../../components/common/ReactivatedBadge/ReactivatedBadge';
import SensitiveStudentBadge from '../../components/common/SensitiveStudentBadge/SensitiveStudentBadge';
import PregnantStudentBadge from '../../components/common/PregnantStudentBadge/PregnantStudentBadge';
import RedFlagBadge from '../../components/common/RedFlagBadge/RedFlagBadge';
import DataTable from '../../components/common/DataTable/DataTable';
import dataTableStyles from '../../components/common/DataTable/DataTable.module.css';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import Pagination from '../../components/common/Pagination/Pagination';
import KebabMenu from '../../components/common/KebabMenu/KebabMenu';
import PageSizeSelect from '../../components/common/PageSizeSelect/PageSizeSelect';
import ViewLinkButton from '../../components/common/ViewLinkButton/ViewLinkButton';
import FormInput from '../../components/common/FormInput/FormInput';
import StudentNotesModal from '../../components/common/StudentNotesModal/StudentNotesModal';
import SearchableSelect, { SelectOption } from '../../components/common/SearchableSelect/SearchableSelect';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import Tooltip from '../../components/common/Tooltip';
import { useOrchestratorData, OrchestratorFilters } from '../../hooks/useOrchestratorData';
import { usePageSize } from '../../hooks/usePageSize';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import { useOrderedFilterSelections } from '../../hooks/useOrderedFilterSelections';
import {
  OrchestratorRow,
  WORKOUT_STATUS_LABELS,
  WORKOUT_STATUS_OPTIONS,
  FUP_STATUS_FILTER_OPTIONS,
  FUP_DATE_FILTER_OPTIONS,
  type OrchestratorFupStatus,
  type OrchestratorFupDateFilter,
  NO_COACH_FILTER_VALUE,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_METHOD_OPTIONS,
  getPaymentMethodLabel,
  StudentNoteItem,
  PaymentStatus,
  WorkoutStatus,
} from '../../types/orchestrator';
import { CoachService } from '../../services/CoachService';
import type { CoachOption } from '../../services/CoachService';
import { PAYMENT_SYSTEM_LABELS } from '../../types/payment';
import {
  StudentClassificationStatus,
  getStudentClassificationBadgeVariant,
  mapStudentClassificationFromColumn,
} from '../../types';
import {
  DEFAULT_STUDENT_TENURE_FILTER,
  type StudentTenureFilter,
} from '../../types/studentTenure';
import { STUDENT_JOURNEY_FILTER_OPTIONS } from '../../constants/commercialFilters';
import { resolveJourneyClassificationFilters } from '../../utils/studentClassificationFilters';
import { StudentService, WorkoutAssignmentService } from '../../services';
import { WorkoutAssignmentRequestError } from '../../services/WorkoutAssignmentService';
import { getBrazilCivilDate } from '../../utils/competence';
import { formatDate, resolveStandardDeliveryDate, validateStudentNote } from '../../utils';
import { useAuth } from '../../contexts/AuthContext';
import { canChangeStudentCoach } from '../../utils/permissions';
import { canWriteStudentObservations } from '../../utils/studentNotesModalAccess';
import OrchestratorAssignmentNotesModal from './OrchestratorAssignmentNotesModal';
import annotationStyles from './OrchestratorAssignmentNotesModal.module.css';
import { useMarketingProjects } from '../../hooks/useMarketingProjects';
import FupCopyLinkButton from './FupCopyLinkButton';
import CopyStudentNameButton from '../../components/common/CopyStudentNameButton/CopyStudentNameButton';
import FupLinkAttemptsList from './FupLinkAttemptsList';
import {
  canViewFupResponse,
  isCycleFupSendAllowedForRow,
  shouldShowFupCopyLinkActions,
  showFupOnboardingAnswersAction,
  skipsFupAsNewStudent,
} from './fupOrchestratorUi';
import { hasUnrespondedFupLink } from './fupLinkDisplay';
import ManagedFupResponseModal from './ManagedFupResponseModal';
import NewStudentAnswersModal from '../../components/students/NewStudentAnswersModal/NewStudentAnswersModal';
import {
  mergeCopyFupLinkResultIntoRow,
  removeFupLinkFromRow,
  rowMatchesCopyFupLinkTarget,
  rowMatchesFupLinkActionTarget,
} from './fupLinks';
import type { CopyFupLinkResult } from '../../services/WorkoutAssignmentService';
import { colors } from '../../theme/colors';
import DatePresetFilter from '../../components/common/DatePresetFilter/DatePresetFilter';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import PageContainer, { PageBody } from '../../components/common/PageContainer/PageContainer';
import {
  ORCHESTRATOR_LAST_WORKOUT_DATE_PRESETS,
  ORCHESTRATOR_PAYMENT_DATE_PRESETS,
} from '../../constants/datePresets';
import styles from './Orchestrator.module.css';
import createSubscriptionModalStyles from '../../components/students/PaymentDetails/CreateSubscriptionModal.module.css';

function paymentLabel(isoDate: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const pay = new Date(`${isoDate.slice(0, 10)}T00:00:00`);
  const diffMs = today.getTime() - pay.getTime();
  const diffDays = Math.round(diffMs / 86400000);
  if (diffDays === 0) return 'Pago hoje';
  if (diffDays === 1) return 'Pago ontem';
  if (diffDays < 7) return `Pago há ${diffDays} dias`;
  return `Pago em ${formatDate(isoDate)}`;
}

function getMaxFupAttemptCount(row: OrchestratorRow): number {
  return (row.fup_links ?? []).reduce(
    (max, link) => Math.max(max, link.attempt_number),
    0,
  );
}

function hasExhaustedFupAttemptsWithoutResponse(row: OrchestratorRow): boolean {
  if (row.has_fup_response) {
    return false;
  }

  return getMaxFupAttemptCount(row) >= 3;
}

const PAID_STATUSES = new Set<PaymentStatus>(['PAID']);

/** Assinatura cancelada com crédito ativo entra na fila; ciclo segue como paga. */
const TRAINING_ENTITLED_PAYMENT_STATUSES = new Set<PaymentStatus>([
  'PAID',
  'SUBSCRIPTION_CANCELED',
]);

function hasConfirmedFupResponse(row: OrchestratorRow): boolean {
  return row.has_fup_response;
}

function entitlesTrainingCycle(row: OrchestratorRow): boolean {
  return TRAINING_ENTITLED_PAYMENT_STATUSES.has(row.payment_status);
}

function hasPendingFupResponse(row: OrchestratorRow): boolean {
  return hasUnrespondedFupLink(row.fup_links ?? [])
    && !hasExhaustedFupAttemptsWithoutResponse(row);
}

const PAYMENT_ICON_MAP: Record<string, { name: string; color: string }> = {
  PAID: { name: 'check_circle', color: colors.solidSuccess },
  PENDING: { name: 'pending', color: colors.solidWarning },
  EXPIRED: { name: 'event_busy', color: colors.solidDanger },
  PAUSED: { name: 'pause_circle', color: 'var(--color-brand-secondary)' },
  SUBSCRIPTION_CANCELED: { name: 'block', color: colors.solidDanger },
};

const PAYMENT_ICON_DEFAULT = { name: 'help', color: 'var(--color-solid-neutral)' };

const PAYMENT_METHOD_ICON_MAP: Record<string, { name: string; color: string }> = {
  pix: { name: 'qr_code_2', color: 'var(--color-solid-info)' },
  credit_card: { name: 'credit_card', color: 'var(--color-brand-primary)' },
};

const STATUS_BADGE_VARIANT: Record<WorkoutStatus, 'success' | 'info' | 'warning' | 'danger' | 'primary'> = {
  awaiting_payment: 'warning',
  payment_failed: 'danger',
  ready_for_workout: 'info',
  awaiting_fup_response: 'warning',
  scheduled: 'primary',
  delivered: 'success',
  canceled: 'danger',
  paused: 'info',
};

interface CalendarModalProps {
  studentName: string;
  lastWorkoutDate?: string | null;
  onConfirm: (date: string) => void;
  onCancel: () => void;
}

const CalendarModal: React.FC<CalendarModalProps> = ({
  studentName,
  lastWorkoutDate,
  onConfirm,
  onCancel,
}) => {
  const [mode, setMode] = useState<'standard' | 'custom'>('standard');
  const brazilToday = getBrazilCivilDate();
  const standardDate = useMemo(() => {
    const [year, month, day] = brazilToday.split('-').map(Number);
    return resolveStandardDeliveryDate(lastWorkoutDate, new Date(year, month - 1, day));
  }, [brazilToday, lastWorkoutDate]);
  const [customDate, setCustomDate] = useState(standardDate);

  return (
    <div className={createSubscriptionModalStyles.overlay} onClick={onCancel}>
      <div className={createSubscriptionModalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={createSubscriptionModalStyles['icon-circle']}>
          <span className="material-symbols-outlined">calendar_month</span>
        </div>

        <h3 className={createSubscriptionModalStyles.title}>Data do Treino — {studentName}</h3>
        <p className={createSubscriptionModalStyles.message}>Selecione a data prevista de entrega.</p>

        <div className={createSubscriptionModalStyles['select-wrap']}>
          <div className={styles['modal-radio-group']}>
            <label className={styles['modal-radio-label']}>
              <input
                type="radio"
                name="dateMode"
                checked={mode === 'standard'}
                onChange={() => setMode('standard')}
              />
              Entrega padrão (4 dias úteis)
            </label>
            <div className={styles['modal-standard-hint']}>{formatDate(standardDate)}</div>
            <label className={styles['modal-radio-label']}>
              <input
                type="radio"
                name="dateMode"
                checked={mode === 'custom'}
                onChange={() => setMode('custom')}
              />
              Personalizado
            </label>
            {mode === 'custom' && (
              <input
                type="date"
                className={styles['modal-date-input']}
                min={brazilToday}
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
              />
            )}
          </div>
        </div>

        <div className={createSubscriptionModalStyles.actions}>
          <button
            type="button"
            className={`${createSubscriptionModalStyles.btn} ${createSubscriptionModalStyles['btn-cancel']}`}
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={`${createSubscriptionModalStyles.btn} ${createSubscriptionModalStyles['btn-confirm']}`}
            onClick={() => onConfirm(mode === 'standard' ? standardDate : customDate)}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

type FupViewModalState = {
  studentId: number;
  studentFupId: number;
  assignmentId: number | null;
  studentName: string;
  fup_created_at: string | null;
};

type NewStudentAnswersModalState = {
  studentId: number;
  studentName: string;
};

type DeliverModalState = {
  assignmentId: number;
  studentName: string;
  deliveredDate: string;
};

type ScheduleSaveConfirmState = {
  workoutAssignmentId: number | null;
  studentId: number;
  studentName: string;
  expectedDeliveryDate: string;
};

type NotesModalState = {
  studentId: number;
  studentName: string;
  newNote: string;
  history: StudentNoteItem[];
  summary: string | null;
};

type AssignmentNotesEditState = {
  studentId: number;
  studentName: string;
  assignmentId: number | null;
  competence: string;
  notes: string;
};

type CoachEditModalState = {
  studentId: number;
  studentName: string;
  coachId: string;
};

function NewStudentBadge({ row }: { row: OrchestratorRow }) {
  const badge = row.classification?.status === StudentClassificationStatus.NEW_STUDENT
    ? row.classification
    : row.is_reactivated
      ? mapStudentClassificationFromColumn(StudentClassificationStatus.NEW_STUDENT)
      : null;

  if (!badge) {
    return null;
  }

  return (
    <Badge
      variant={getStudentClassificationBadgeVariant(badge)}
      className={styles['new-student-badge']}
    >
      {badge.label}
    </Badge>
  );
}

function shouldOpenNewStudentOnboardingAnswers(row: OrchestratorRow): boolean {
  if (!skipsFupAsNewStudent(row)) {
    return false;
  }

  return (row.fup_links ?? []).length === 0;
}

function getDisplayedCycleStatus(row: OrchestratorRow): WorkoutStatus {
  if (row.payment_status === 'PAUSED') {
    return 'paused';
  }

  if (row.workout_status === 'scheduled' || row.workout_status === 'canceled') {
    return row.workout_status;
  }

  if (
    row.workout_status === 'delivered'
    && (!isCycleFupSendAllowedForRow(row) || skipsFupAsNewStudent(row))
  ) {
    return 'delivered';
  }

  if (skipsFupAsNewStudent(row)) {
    return 'ready_for_workout';
  }

  if (
    hasConfirmedFupResponse(row)
    || (entitlesTrainingCycle(row) && hasExhaustedFupAttemptsWithoutResponse(row))
    || (
      row.workout_status === 'ready_for_workout'
      && !isCycleFupSendAllowedForRow(row)
      && !hasPendingFupResponse(row)
    )
  ) {
    return 'ready_for_workout';
  }

  if (hasPendingFupResponse(row)) {
    return 'awaiting_fup_response';
  }

  if (isCycleFupSendAllowedForRow(row)) {
    return 'awaiting_fup_response';
  }

  if (row.workout_status === 'delivered' || row.workout_status === 'ready_for_workout') {
    return row.workout_status;
  }

  if (entitlesTrainingCycle(row)) {
    return 'ready_for_workout';
  }

  if (row.payment_status === 'EXPIRED') {
    return 'payment_failed';
  }

  return 'awaiting_payment';
}

const OrchestratorMobileSectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SectionTitle as="span" className={styles['orc-mobile-section-label']}>
    {children}
  </SectionTitle>
);

const PaymentCell: React.FC<{ row: OrchestratorRow }> = ({ row }) => {
  const icon = PAYMENT_ICON_MAP[row.payment_status] ?? PAYMENT_ICON_DEFAULT;
  const isPaid = PAID_STATUSES.has(row.payment_status);
  const hasPayerName = Boolean(row.payer_name && row.payer_name.trim());
  const gatewayKey = row.payment_system?.trim().toLowerCase() as keyof typeof PAYMENT_SYSTEM_LABELS | undefined;
  const gatewayLabel = gatewayKey && PAYMENT_SYSTEM_LABELS[gatewayKey]
    ? PAYMENT_SYSTEM_LABELS[gatewayKey]
    : (row.payment_system?.trim() || '—');

  return (
    <div className={styles['col-payment']} data-dt-col="3" data-card-label="">
      <OrchestratorMobileSectionLabel>Pagamento</OrchestratorMobileSectionLabel>
      <div className={styles['orc-mobile-section-body']}>
        <Icon name={icon.name} size={20} color={icon.color} filled className={styles['payment-status-icon']} />
        <div className={styles['payment-info']}>
        {row.payment_status === 'PAUSED' && row.active_pause ? (
          <>
            <Badge variant="info" solid>
              Pausada até {formatDate(row.active_pause.paused_until)}
            </Badge>
            <span className={styles['payment-text']}>
              Retorno em {formatDate(row.active_pause.scheduled_resume_date)}
            </span>
          </>
        ) : isPaid && row.last_payment_date ? (
          <>
            <span className={`${styles['payment-text']} ${styles['payment-text-desktop']}`}>
              <strong>{paymentLabel(row.last_payment_date)}</strong> ({formatDate(row.last_payment_date)})
            </span>
            <span className={`${styles['payment-text']} ${styles['payment-text-mobile']}`}>
              <strong>Pago em:</strong> {formatDate(row.last_payment_date)}
              <Icon name={icon.name} size={20} color={icon.color} filled className={styles['payment-status-icon-inline']} />
            </span>
            {hasPayerName && (
              <span className={styles['payment-text']}>
                <strong>Pagador:</strong> {row.payer_name}
              </span>
            )}
          </>
        ) : (
          <span className={styles['payment-text']}>
            {PAYMENT_STATUS_LABELS[row.payment_status]}
          </span>
        )}
        {row.has_consultancy_credit ? (
          <>
            <Badge variant="info" solid>
              Crédito de consultoria
            </Badge>
            {row.consultancy_credit_valid_until ? (
              <span className={styles['payment-text']}>
                Válido até {formatDate(row.consultancy_credit_valid_until)}
              </span>
            ) : null}
          </>
        ) : null}
        <span className={styles['payment-text']}>
          <strong>Gateway:</strong> {gatewayLabel}
        </span>
        <span className={styles['payment-text']}>
          <strong>Forma:</strong> {getPaymentMethodLabel(row.payment_method)}
        </span>
        </div>
      </div>
    </div>
  );
};

type PendingFupLinkRemoval = {
  studentId: number;
  assignmentId: number | null;
  competence: string;
  token: string;
};

const FupCell: React.FC<{
  row: OrchestratorRow;
  onViewResponse: (row: OrchestratorRow) => void;
  onLinkCopied: (params: {
    studentId: number;
    assignmentId: number | null;
    competence: string;
  }, result: CopyFupLinkResult) => void;
  onRemoveLinkRequest: (params: PendingFupLinkRemoval) => void;
}> = ({ row, onViewResponse, onLinkCopied, onRemoveLinkRequest }) => {
  const isNewStudent = skipsFupAsNewStudent(row);

  if (row.payment_status === 'PAUSED') {
    return (
      <div className={styles['col-fup']} data-dt-col="5" data-card-label="">
        <OrchestratorMobileSectionLabel>FUP</OrchestratorMobileSectionLabel>
        <div className={styles['orc-mobile-section-body']}>
          <Badge variant="info">FUP suspenso</Badge>
        </div>
      </div>
    );
  }

  const fupLinks = row.fup_links ?? [];
  const showAnsweredAction = canViewFupResponse(row);
  const showOnboardingAnswers = showFupOnboardingAnswersAction(row, isNewStudent);
  const showPendingActions = shouldShowFupCopyLinkActions(row, isNewStudent);

  return (
  <div className={styles['col-fup']} data-dt-col="5" data-card-label="">
    <OrchestratorMobileSectionLabel>FUP</OrchestratorMobileSectionLabel>
    <div className={styles['orc-mobile-section-body']}>
    <div className={styles['fup-actions']}>
      {(showAnsweredAction || showOnboardingAnswers) && (
        <ViewLinkButton
          label="Ver resposta"
          icon={null}
          onClick={() => onViewResponse(row)}
        />
      )}
      {showPendingActions && (
        <>
          <div className={styles['fup-response-indicator']} aria-label="FUP sem resposta">
            Sem resposta
          </div>
          <FupCopyLinkButton
            studentId={row.student_id}
            assignmentId={row.workout_assignment_id ?? null}
            competence={row.competence}
            onLinkCopied={(result) => onLinkCopied({
              studentId: row.student_id,
              assignmentId: row.workout_assignment_id ?? null,
              competence: row.competence,
            }, result)}
          />
        </>
      )}
    </div>
    {fupLinks.length > 0 && (
      <div className={styles['fup-progress-row']}>
        <FupLinkAttemptsList
          links={fupLinks}
          onRemoveRequest={(link) => onRemoveLinkRequest({
            studentId: row.student_id,
            assignmentId: row.workout_assignment_id ?? null,
            competence: row.competence,
            token: link.token,
          })}
        />
      </div>
    )}
    </div>
  </div>
  );
};

const Orchestrator: React.FC = () => {
  const navigate = useNavigate();
  const { roles } = useAuth();
  const canChangeCoach = canChangeStudentCoach(roles);
  const canWriteObservations = canWriteStudentObservations(roles);
  const [pendingAssignmentNotesEdit, setPendingAssignmentNotesEdit] = useState<AssignmentNotesEditState | null>(null);
  const [savingAssignmentNotes, setSavingAssignmentNotes] = useState(false);
  const querySchema = useMemo(() => ({
    page: queryFilter.integer(1, { min: 1 }),
    searchName: queryFilter.string(),
    sortOrder: queryFilter.enum<'asc' | 'desc'>('asc', ['asc', 'desc']),
    paymentFilters: queryFilter.array<string>(),
    paymentMethodFilters: queryFilter.array<string>(),
    gatewayFilters: queryFilter.array<string>(),
    coachFilters: queryFilter.array<string>(),
    workoutStatusFilters: queryFilter.array<string>(),
    fupStatus: queryFilter.array<OrchestratorFupStatus>([], {
      allowed: FUP_STATUS_FILTER_OPTIONS.map((option) => option.value),
    }),
    fupDateFilter: queryFilter.array<OrchestratorFupDateFilter>([], {
      allowed: FUP_DATE_FILTER_OPTIONS.map((option) => option.value),
    }),
    studentTenure: queryFilter.enum<StudentTenureFilter>(
      DEFAULT_STUDENT_TENURE_FILTER,
      ['all', 'new', 'established'],
    ),
    classifications: queryFilter.array<string>(),
    dateStart: queryFilter.string(),
    dateEnd: queryFilter.string(),
    dueDateStart: queryFilter.string(),
    dueDateEnd: queryFilter.string(),
    lastWorkoutDateStart: queryFilter.string(),
    lastWorkoutDateEnd: queryFilter.string(),
    projectCodes: queryFilter.array<string>(),
  }), []);
  const {
    filters: queryFilters,
    setFilter,
    setFilters,
  } = useQueryFilters(querySchema, { pageKey: 'page' });
  const {
    searchName,
    sortOrder,
    paymentFilters,
    paymentMethodFilters,
    gatewayFilters,
    coachFilters,
    workoutStatusFilters,
    fupStatus,
    fupDateFilter,
    studentTenure: studentTenureFilter,
    classifications: classificationFilters,
    dateStart,
    dateEnd,
    dueDateStart,
    dueDateEnd,
    lastWorkoutDateStart,
    lastWorkoutDateEnd,
    projectCodes: projectCodeFilters,
  } = queryFilters;

  const {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
  } = usePageSize('orchestrator_page_size');
  const [coachOptions, setCoachOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [coachAssignmentOptions, setCoachAssignmentOptions] = useState<CoachOption[]>([]);
  const [loadingCoachOptions, setLoadingCoachOptions] = useState(false);
  const [pendingFupView, setPendingFupView] = useState<FupViewModalState | null>(null);
  const [pendingNewStudentAnswers, setPendingNewStudentAnswers] = useState<NewStudentAnswersModalState | null>(null);
  const [pendingDeliver, setPendingDeliver] = useState<DeliverModalState | null>(null);
  const [pendingScheduleSave, setPendingScheduleSave] = useState<ScheduleSaveConfirmState | null>(null);
  const [pendingNotesSave, setPendingNotesSave] = useState<NotesModalState | null>(null);
  const [pendingCoachEdit, setPendingCoachEdit] = useState<CoachEditModalState | null>(null);
  const [pendingFupLinkRemoval, setPendingFupLinkRemoval] = useState<PendingFupLinkRemoval | null>(null);
  const [submittingDeliver, setSubmittingDeliver] = useState(false);
  const [deliverDateError, setDeliverDateError] = useState<string | undefined>();
  const [scheduleDateError, setScheduleDateError] = useState<{ studentId: number; message: string } | undefined>();
  const [submittingNotes, setSubmittingNotes] = useState(false);
  const [loadingNotesHistory, setLoadingNotesHistory] = useState(false);
  const [submittingCoachEdit, setSubmittingCoachEdit] = useState(false);
  const [submittingFupLinkRemoval, setSubmittingFupLinkRemoval] = useState(false);

  const handleDateChange = useCallback((start: string, end: string) => {
    setFilters({ dateStart: start, dateEnd: end });
  }, [setFilters]);

  const handleDueDateChange = useCallback((start: string, end: string) => {
    setFilters({ dueDateStart: start, dueDateEnd: end });
  }, [setFilters]);

  const handleLastWorkoutDateChange = useCallback((start: string, end: string) => {
    setFilters({ lastWorkoutDateStart: start, lastWorkoutDateEnd: end });
  }, [setFilters]);

  const { orderedSelections, updateFilter, clearAll: clearOrderedSelections } = useOrderedFilterSelections();

  const handlePaymentFiltersChange = useCallback((newValues: string[]) => {
    updateFilter('payment', newValues, paymentFilters);
    setFilter('paymentFilters', newValues);
  }, [paymentFilters, setFilter, updateFilter]);

  const handlePaymentMethodFiltersChange = useCallback((newValues: string[]) => {
    updateFilter('paymentMethod', newValues, paymentMethodFilters);
    setFilter('paymentMethodFilters', newValues);
  }, [paymentMethodFilters, setFilter, updateFilter]);

  const handleGatewayFiltersChange = useCallback((newValues: string[]) => {
    updateFilter('gateway', newValues, gatewayFilters);
    setFilter('gatewayFilters', newValues);
  }, [gatewayFilters, setFilter, updateFilter]);

  const handleCoachFiltersChange = useCallback((newValues: string[]) => {
    updateFilter('coach', newValues, coachFilters);
    setFilter('coachFilters', newValues);
  }, [coachFilters, setFilter, updateFilter]);

  const handleWorkoutStatusFiltersChange = useCallback((newValues: string[]) => {
    updateFilter('workoutStatus', newValues, workoutStatusFilters);
    setFilter('workoutStatusFilters', newValues);
  }, [setFilter, updateFilter, workoutStatusFilters]);

  const handleFupStatusFiltersChange = useCallback((newValues: string[]) => {
    updateFilter('fupStatus', newValues, fupStatus);
    setFilter('fupStatus', newValues as OrchestratorFupStatus[]);
  }, [fupStatus, setFilter, updateFilter]);

  const handleFupDateFilterChange = useCallback((newValues: string[]) => {
    const allowed = FUP_DATE_FILTER_OPTIONS.map((option) => option.value);
    const selected = newValues.filter((value): value is OrchestratorFupDateFilter => (
      allowed.includes(value as OrchestratorFupDateFilter)
    ));
    const latest = selected.length > 0 ? selected[selected.length - 1] : null;
    const normalized = latest ? [latest] : [];
    updateFilter('fupDate', normalized, fupDateFilter);
    setFilter('fupDateFilter', normalized);
  }, [fupDateFilter, setFilter, updateFilter]);

  const handleProjectFiltersChange = useCallback((newValues: string[]) => {
    updateFilter('project', newValues, projectCodeFilters);
    setFilter('projectCodes', newValues);
  }, [projectCodeFilters, setFilter, updateFilter]);

  const journeyFilters = useMemo(
    () => resolveJourneyClassificationFilters(classificationFilters, studentTenureFilter),
    [classificationFilters, studentTenureFilter],
  );

  const handleClassificationFiltersChange = useCallback((newValues: string[]) => {
    updateFilter('classifications', newValues, journeyFilters);
    setFilters({
      classifications: newValues,
      studentTenure: DEFAULT_STUDENT_TENURE_FILTER,
    });
  }, [journeyFilters, setFilters, updateFilter]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      searchName: '',
      paymentFilters: [],
      paymentMethodFilters: [],
      gatewayFilters: [],
      coachFilters: [],
      workoutStatusFilters: [],
      fupStatus: [],
      fupDateFilter: [],
      studentTenure: DEFAULT_STUDENT_TENURE_FILTER,
      classifications: [],
      dateStart: '',
      dateEnd: '',
      dueDateStart: '',
      dueDateEnd: '',
      lastWorkoutDateStart: '',
      lastWorkoutDateEnd: '',
      projectCodes: [],
    });
    clearOrderedSelections();
  }, [clearOrderedSelections, setFilters]);

  useEffect(() => {
    let isCancelled = false;

    const loadCoaches = async () => {
      try {
        setLoadingCoachOptions(true);
        const options = await CoachService.listOptions();

        if (isCancelled) {
          return;
        }

        setCoachOptions([
          ...options.map((option) => ({ value: String(option.id), label: option.label })),
          { value: NO_COACH_FILTER_VALUE, label: 'Sem professora' },
        ]);
        setCoachAssignmentOptions(options);
      } catch {
        if (!isCancelled) {
          setCoachOptions([{ value: NO_COACH_FILTER_VALUE, label: 'Sem professora' }]);
          setCoachAssignmentOptions([]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingCoachOptions(false);
        }
      }
    };

    void loadCoaches();

    return () => {
      isCancelled = true;
    };
  }, []);

  const coachSelectOptions = useMemo<SelectOption[]>(
    () => coachAssignmentOptions.map((coach) => ({ value: String(coach.id), label: coach.label })),
    [coachAssignmentOptions],
  );

  const projects = useMarketingProjects();
  const projectFilterOptions = useMemo(
    () => projects.map((project) => ({
      value: project.code,
      label: project.code,
      color: project.colors.background,
    })),
    [projects],
  );

  const paymentOptions = useMemo(() => PAYMENT_STATUS_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    icon: PAYMENT_ICON_MAP[option.value]?.name,
    iconColor: PAYMENT_ICON_MAP[option.value]?.color,
  })), []);

  const paymentMethodOptions = useMemo(() => PAYMENT_METHOD_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    icon: PAYMENT_METHOD_ICON_MAP[option.value]?.name,
    iconColor: PAYMENT_METHOD_ICON_MAP[option.value]?.color,
  })), []);

  const gatewayOptions = useMemo(() => (
    ['asaas', 'stripe', 'paypal', 'pix'].map((value) => ({
      value,
      label: PAYMENT_SYSTEM_LABELS[value as keyof typeof PAYMENT_SYSTEM_LABELS] ?? value,
    }))
  ), []);

  const workoutStatusOptions = useMemo(() => WORKOUT_STATUS_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
  })), []);

  const fupStatusOptions = useMemo(() => FUP_STATUS_FILTER_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    ...('color' in option ? { color: option.color } : {}),
  })), []);

  const fupDateFilterOptions = useMemo(() => FUP_DATE_FILTER_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
  })), []);

  const allOptionsMap = useMemo(() => {
    const map: Record<string, Record<string, { label: string; icon?: string; iconColor?: string; color?: string }>> = {};
    map.payment = {};
    for (const option of paymentOptions) map.payment[option.value] = option;
    map.paymentMethod = {};
    for (const option of paymentMethodOptions) map.paymentMethod[option.value] = option;
    map.gateway = {};
    for (const option of gatewayOptions) map.gateway[option.value] = option;
    map.coach = {};
    for (const option of coachOptions) map.coach[option.value] = option;
    map.workoutStatus = {};
    for (const option of workoutStatusOptions) map.workoutStatus[option.value] = option;
    map.fupStatus = {};
    for (const option of fupStatusOptions) map.fupStatus[option.value] = option;
    map.fupDate = {};
    for (const option of fupDateFilterOptions) map.fupDate[option.value] = option;
    map.project = {};
    for (const option of projectFilterOptions) map.project[option.value] = option;
    map.classifications = {};
    for (const option of STUDENT_JOURNEY_FILTER_OPTIONS) map.classifications[option.value] = option;
    return map;
  }, [paymentOptions, paymentMethodOptions, gatewayOptions, coachOptions, workoutStatusOptions, fupStatusOptions, fupDateFilterOptions, projectFilterOptions]);

  const handleRemoveChip = useCallback((filterKey: string, value: string) => {
    if (filterKey === 'payment') {
      const newValues = paymentFilters.filter((filterValue) => filterValue !== value);
      updateFilter('payment', newValues, paymentFilters);
      setFilter('paymentFilters', newValues);
    } else if (filterKey === 'paymentMethod') {
      const newValues = paymentMethodFilters.filter((filterValue) => filterValue !== value);
      updateFilter('paymentMethod', newValues, paymentMethodFilters);
      setFilter('paymentMethodFilters', newValues);
    } else if (filterKey === 'gateway') {
      const newValues = gatewayFilters.filter((filterValue) => filterValue !== value);
      updateFilter('gateway', newValues, gatewayFilters);
      setFilter('gatewayFilters', newValues);
    } else if (filterKey === 'coach') {
      const newValues = coachFilters.filter((filterValue) => filterValue !== value);
      updateFilter('coach', newValues, coachFilters);
      setFilter('coachFilters', newValues);
    } else if (filterKey === 'workoutStatus') {
      const newValues = workoutStatusFilters.filter((filterValue) => filterValue !== value);
      updateFilter('workoutStatus', newValues, workoutStatusFilters);
      setFilter('workoutStatusFilters', newValues);
    } else if (filterKey === 'fupStatus') {
      const newValues = fupStatus.filter((filterValue) => filterValue !== value);
      updateFilter('fupStatus', newValues, fupStatus);
      setFilter('fupStatus', newValues);
    } else if (filterKey === 'fupDate') {
      updateFilter('fupDate', [], fupDateFilter);
      setFilter('fupDateFilter', []);
    } else if (filterKey === 'project') {
      const newValues = projectCodeFilters.filter((filterValue) => filterValue !== value);
      updateFilter('project', newValues, projectCodeFilters);
      setFilter('projectCodes', newValues);
    } else if (filterKey === 'classifications') {
      const newValues = journeyFilters.filter((filterValue) => filterValue !== value);
      updateFilter('classifications', newValues, journeyFilters);
      setFilters({
        classifications: newValues,
        studentTenure: DEFAULT_STUDENT_TENURE_FILTER,
      });
    }
  }, [paymentFilters, paymentMethodFilters, gatewayFilters, coachFilters, workoutStatusFilters, fupStatus, fupDateFilter, projectCodeFilters, journeyFilters, setFilter, setFilters, updateFilter]);

  const hasActiveFilters = !!(
    searchName
    || paymentFilters.length > 0
    || paymentMethodFilters.length > 0
    || gatewayFilters.length > 0
    || coachFilters.length > 0
    || workoutStatusFilters.length > 0
    || fupStatus.length > 0
    || fupDateFilter.length > 0
    || projectCodeFilters.length > 0
    || journeyFilters.length > 0
    || dateStart
    || dateEnd
    || dueDateStart
    || dueDateEnd
    || lastWorkoutDateStart
    || lastWorkoutDateEnd
  );

  const selectionsForChips = useMemo(() => {
    const selections = [...orderedSelections];
    const currentSelections = [
      ['payment', paymentFilters],
      ['paymentMethod', paymentMethodFilters],
      ['gateway', gatewayFilters],
      ['coach', coachFilters],
      ['workoutStatus', workoutStatusFilters],
      ['fupStatus', fupStatus],
      ['fupDate', fupDateFilter],
      ['project', projectCodeFilters],
      ['classifications', journeyFilters],
    ] as const;

    currentSelections.forEach(([filterKey, values]) => {
      values.forEach((value) => {
        if (!selections.some((selection) => (
          selection.filterKey === filterKey && selection.value === value
        ))) {
          selections.push({ filterKey, value });
        }
      });
    });

    return selections;
  }, [coachFilters, fupStatus, fupDateFilter, projectCodeFilters, gatewayFilters, journeyFilters, orderedSelections, paymentFilters, paymentMethodFilters, workoutStatusFilters]);

  const unifiedChips: FilterChipEntry[] = useMemo(() => {
    return compactFilterChips(
      selectionsForChips
        .filter((selection) => {
          if (selection.filterKey === 'payment') return paymentFilters.includes(selection.value);
          if (selection.filterKey === 'paymentMethod') return paymentMethodFilters.includes(selection.value);
          if (selection.filterKey === 'gateway') return gatewayFilters.includes(selection.value);
          if (selection.filterKey === 'coach') return coachFilters.includes(selection.value);
          if (selection.filterKey === 'workoutStatus') return workoutStatusFilters.includes(selection.value);
          if (selection.filterKey === 'fupStatus') return fupStatus.includes(selection.value as OrchestratorFupStatus);
          if (selection.filterKey === 'fupDate') return fupDateFilter.includes(selection.value as OrchestratorFupDateFilter);
          if (selection.filterKey === 'project') return projectCodeFilters.includes(selection.value);
          if (selection.filterKey === 'classifications') return journeyFilters.includes(selection.value);
          return false;
        })
        .map((selection) => {
          const option = allOptionsMap[selection.filterKey]?.[selection.value];
          return {
            filterKey: selection.filterKey,
            value: selection.value,
            label: option?.label ?? selection.value,
            icon: option?.icon,
            iconColor: option?.iconColor,
            color: option?.color,
            onRemove: (valueToRemove: string) => handleRemoveChip(selection.filterKey, valueToRemove),
          };
        }),
    );
  }, [selectionsForChips, paymentFilters, paymentMethodFilters, gatewayFilters, coachFilters, workoutStatusFilters, fupStatus, fupDateFilter, projectCodeFilters, journeyFilters, allOptionsMap, handleRemoveChip]);

  const filters: OrchestratorFilters = useMemo(
    () => ({
      searchName: searchName || undefined,
      filterPaymentStatus: paymentFilters.length > 0 ? paymentFilters : undefined,
      filterPaymentMethod: paymentMethodFilters.length > 0 ? paymentMethodFilters : undefined,
      filterGateway: gatewayFilters.length > 0 ? gatewayFilters : undefined,
      filterCoachIds: coachFilters.length > 0 ? coachFilters : undefined,
      filterWorkoutStatus: workoutStatusFilters.length > 0 ? workoutStatusFilters : undefined,
      filterFupStatus: fupStatus.length > 0 ? fupStatus : undefined,
      filterFupDate: fupDateFilter[0] ?? undefined,
      classifications: journeyFilters.length > 0 ? journeyFilters : undefined,
      customDateStart: dateStart || undefined,
      customDateEnd: dateEnd || undefined,
      dueDateStart: dueDateStart || undefined,
      dueDateEnd: dueDateEnd || undefined,
      lastWorkoutDateStart: lastWorkoutDateStart || undefined,
      lastWorkoutDateEnd: lastWorkoutDateEnd || undefined,
      projectCodes: projectCodeFilters.length > 0 ? projectCodeFilters : undefined,
      sortOrder,
    }),
    [searchName, paymentFilters, paymentMethodFilters, gatewayFilters, coachFilters, workoutStatusFilters, fupStatus, fupDateFilter, journeyFilters, dateStart, dateEnd, dueDateStart, dueDateEnd, lastWorkoutDateStart, lastWorkoutDateEnd, projectCodeFilters, sortOrder],
  );

  const { rows: fetchedRows, pagination, loading, error, refetch } = useOrchestratorData(
    page,
    pageSize,
    filters,
  );
  const [rows, setRows] = useState<OrchestratorRow[]>([]);
  const [changedIds, setChangedIds] = useState<Set<number>>(new Set());
  const changedIdsRef = useRef<Set<number>>(new Set());
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [modalRowId, setModalRowId] = useState<number | null>(null);

  useEffect(() => {
    setRows((currentRows) => {
      const pendingIds = changedIdsRef.current;

      if (pendingIds.size === 0) {
        return fetchedRows;
      }

      return fetchedRows.map((fetchedRow) => {
        if (!pendingIds.has(fetchedRow.student_id)) {
          return fetchedRow;
        }

        const currentRow = currentRows.find((row) => row.student_id === fetchedRow.student_id);
        return currentRow
          ? { ...fetchedRow, expected_delivery_date: currentRow.expected_delivery_date }
          : fetchedRow;
      });
    });
    setChangedIds((currentIds) => {
      const fetchedIds = new Set(fetchedRows.map((row) => row.student_id));
      const nextIds = new Set<number>();

      currentIds.forEach((id) => {
        if (fetchedIds.has(id)) {
          nextIds.add(id);
        }
      });

      changedIdsRef.current = nextIds;
      return nextIds;
    });
  }, [fetchedRows]);

  const setExpectedDeliveryDate = useCallback((studentId: number, expectedDeliveryDate: string) => {
    setRows((currentRows) => currentRows.map((row) => (
      row.student_id === studentId
        ? { ...row, expected_delivery_date: expectedDeliveryDate }
        : row
    )));
    setChangedIds((currentIds) => {
      const nextIds = new Set(currentIds).add(studentId);
      changedIdsRef.current = nextIds;
      return nextIds;
    });
    setScheduleDateError((current) => (
      current?.studentId === studentId ? undefined : current
    ));
  }, []);

  const handleScheduleSave = useCallback(async (studentId: number) => {
    const row = rows.find((currentRow) => currentRow.student_id === studentId);
    if (!row?.expected_delivery_date) {
      return false;
    }

    setSavingIds((currentIds) => new Set(currentIds).add(studentId));
    setScheduleDateError(undefined);
    try {
      await WorkoutAssignmentService.schedule({
        assignmentId: row.workout_assignment_id ?? null,
        studentId: row.student_id,
        expectedDeliveryDate: row.expected_delivery_date,
      });
      setRows((currentRows) => currentRows.map((currentRow) => (
        currentRow.student_id === studentId
          ? { ...currentRow, workout_status: 'scheduled' }
          : currentRow
      )));
      setChangedIds((currentIds) => {
        const nextIds = new Set(currentIds);
        nextIds.delete(studentId);
        changedIdsRef.current = nextIds;
        return nextIds;
      });
      refetch();
      return true;
    } catch (error) {
      if (error instanceof WorkoutAssignmentRequestError && error.fields.expected_delivery_date) {
        setScheduleDateError({
          studentId,
          message: error.fields.expected_delivery_date,
        });
        return false;
      }
      alert(error instanceof Error ? error.message : 'Não foi possível salvar a data prevista.');
      return false;
    } finally {
      setSavingIds((currentIds) => {
        const nextIds = new Set(currentIds);
        nextIds.delete(studentId);
        return nextIds;
      });
    }
  }, [refetch, rows]);

  const reloadNotesHistory = useCallback(async (studentId: number) => {
    const { notes: history, summary } = await WorkoutAssignmentService.listStudentNotes(studentId);
    setPendingNotesSave((current) => (
      current && current.studentId === studentId
        ? { ...current, history, summary }
        : current
    ));
  }, []);

  const openStudentNotesModal = useCallback(async (params: {
    studentId: number;
    studentName: string;
  }) => {
    setPendingNotesSave({
      studentId: params.studentId,
      studentName: params.studentName,
      newNote: '',
      history: [],
      summary: null,
    });
    setLoadingNotesHistory(true);

    try {
      const { notes: history, summary } = await WorkoutAssignmentService.listStudentNotes(params.studentId);
      setPendingNotesSave({
        studentId: params.studentId,
        studentName: params.studentName,
        newNote: '',
        history,
        summary,
      });
    } catch (error) {
      setPendingNotesSave(null);
      alert(error instanceof Error ? error.message : 'Não foi possível carregar as observações.');
    } finally {
      setLoadingNotesHistory(false);
    }
  }, []);

  const handleNotesSaveConfirm = useCallback(async () => {
    if (!pendingNotesSave) return;

    const trimmedNote = pendingNotesSave.newNote.trim();
    const noteError = validateStudentNote(trimmedNote);
    if (noteError) {
      alert(noteError);
      return;
    }

    setSubmittingNotes(true);
    try {
      await WorkoutAssignmentService.saveNotes({
        studentId: pendingNotesSave.studentId,
        note: trimmedNote,
      });
      setPendingNotesSave(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Não foi possível salvar as observações.');
    } finally {
      setSubmittingNotes(false);
    }
  }, [pendingNotesSave]);

  const handleAssignmentNotesSave = useCallback(async (notes: string) => {
    if (!pendingAssignmentNotesEdit) {
      return;
    }

    const { assignmentId, studentId, competence } = pendingAssignmentNotesEdit;
    setSavingAssignmentNotes(true);
    try {
      const saved = await WorkoutAssignmentService.updateAssignmentNotes({
        assignmentId,
        studentId,
        competence,
        notes,
      });
      setRows((currentRows) => currentRows.map((row) => (
        row.student_id === studentId
          ? {
            ...row,
            id: saved.assignmentId,
            workout_assignment_id: saved.assignmentId,
            workout_status: saved.workoutStatus ?? row.workout_status,
            notes: saved.notes,
          }
          : row
      )));
      setPendingAssignmentNotesEdit(null);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível salvar as anotações.');
    } finally {
      setSavingAssignmentNotes(false);
    }
  }, [pendingAssignmentNotesEdit]);

  const handleDeleteNote = useCallback(async (noteId: number) => {
    const studentId = pendingNotesSave?.studentId;
    if (!studentId) {
      return;
    }

    await WorkoutAssignmentService.deleteNote(noteId);
    await reloadNotesHistory(studentId);
  }, [pendingNotesSave?.studentId, reloadNotesHistory]);

  const handleCoachEditConfirm = useCallback(async () => {
    if (!pendingCoachEdit) {
      return;
    }

    const selectedCoachId = Number.parseInt(pendingCoachEdit.coachId, 10);
    if (!Number.isFinite(selectedCoachId) || selectedCoachId <= 0) {
      alert('Selecione uma professora.');
      return;
    }

    setSubmittingCoachEdit(true);
    try {
      const updatedCoach = await StudentService.upsertCoachAssociation(
        pendingCoachEdit.studentId,
        selectedCoachId,
      );

      setRows((currentRows) => currentRows.map((row) => (
        row.student_id === pendingCoachEdit.studentId
          ? {
            ...row,
            coach_id: updatedCoach.coach_id,
            coach_name: updatedCoach.coach_name,
          }
          : row
      )));
      setPendingCoachEdit(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Não foi possível atualizar a professora.');
    } finally {
      setSubmittingCoachEdit(false);
    }
  }, [pendingCoachEdit]);

  const handleFupLinkCopied = useCallback((
    params: {
      studentId: number;
      assignmentId: number | null;
      competence: string;
    },
    result: CopyFupLinkResult,
  ) => {
    setRows((currentRows) => currentRows.map((row) => (
      rowMatchesCopyFupLinkTarget(row, params, result)
        ? mergeCopyFupLinkResultIntoRow(row, result)
        : row
    )));
  }, []);

  const handleFupLinkRemoveRequest = useCallback((params: PendingFupLinkRemoval) => {
    setPendingFupLinkRemoval(params);
  }, []);

  const handleFupLinkRemoveConfirm = useCallback(async () => {
    if (!pendingFupLinkRemoval) {
      return;
    }

    setSubmittingFupLinkRemoval(true);

    try {
      await WorkoutAssignmentService.removeFupLink(pendingFupLinkRemoval.token);

      setRows((currentRows) => currentRows.map((row) => (
        rowMatchesFupLinkActionTarget(row, pendingFupLinkRemoval)
          ? removeFupLinkFromRow(row, pendingFupLinkRemoval.token)
          : row
      )));
      setPendingFupLinkRemoval(null);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível remover o link de FUP.');
    } finally {
      setSubmittingFupLinkRemoval(false);
    }
  }, [pendingFupLinkRemoval]);

  const openFupViewModal = useCallback((row: OrchestratorRow) => {
    if (shouldOpenNewStudentOnboardingAnswers(row)) {
      setPendingNewStudentAnswers({
        studentId: row.student_id,
        studentName: row.student_name,
      });
      return;
    }

    if (!canViewFupResponse(row)) {
      return;
    }

    setPendingFupView({
      studentId: row.student_id,
      studentFupId: row.student_fup_id,
      assignmentId: row.workout_assignment_id ?? null,
      studentName: row.student_name,
      fup_created_at: row.fup_created_at,
    });
  }, []);

  const closeFupView = useCallback(() => {
    setPendingFupView(null);
  }, []);

  const handleFupSaved = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleFupDeleted = useCallback((result: { studentFupId: number; assignmentId: number | null }) => {
    setRows((currentRows) => currentRows.map((row) => (
      row.student_fup_id === result.studentFupId
      || (result.assignmentId != null && row.workout_assignment_id === result.assignmentId)
        ? {
          ...row,
          has_fup_response: false,
          student_fup_id: null,
          fup_created_at: null,
        }
        : row
    )));
  }, []);

  const handleDeliverConfirm = useCallback(async () => {
    if (!pendingDeliver) return;

    setSubmittingDeliver(true);
    setDeliverDateError(undefined);
    try {
      await WorkoutAssignmentService.deliver({
        assignmentId: pendingDeliver.assignmentId,
        deliveredDate: pendingDeliver.deliveredDate,
      });
      setPendingDeliver(null);
      refetch();
    } catch (error) {
      if (error instanceof WorkoutAssignmentRequestError && error.fields.delivered_date) {
        setDeliverDateError(error.fields.delivered_date);
        return;
      }
      alert(error instanceof Error ? error.message : 'Não foi possível entregar o treino.');
    } finally {
      setSubmittingDeliver(false);
    }
  }, [pendingDeliver, refetch]);

  const handleScheduleSaveConfirm = useCallback(async () => {
    if (!pendingScheduleSave) return;

    await handleScheduleSave(pendingScheduleSave.studentId);
    setPendingScheduleSave(null);
  }, [handleScheduleSave, pendingScheduleSave]);

  const modalRow = modalRowId !== null
    ? rows.find((row) => row.student_id === modalRowId) ?? null
    : null;

  if (error) {
    return <EmptyState message={`Erro: ${error}`} />;
  }

  return (
    <PageContainer>
      <FilterBar gap="sm" pushFirstChild>
        <RecordCount
          label="Alunas:"
          count={`${pagination.totalRecords} alunas`}
          className={styles['student-count']}
        />
        <FilterGroup>
          <SearchFilterBar
            searchValue={searchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Pesquisar"
            loading={loading}
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            groups={[
              {
                title: 'Gateway',
                values: gatewayFilters,
                onChange: handleGatewayFiltersChange,
                options: gatewayOptions,
              },
              {
                title: 'Forma de pagamento',
                values: paymentMethodFilters,
                onChange: handlePaymentMethodFiltersChange,
                options: paymentMethodOptions,
              },
              {
                title: 'Status',
                values: paymentFilters,
                onChange: handlePaymentFiltersChange,
                options: paymentOptions,
              },
            ]}
            placeholder="Pagamento"
            clearButtonLabel="Limpar filtro"
            hideChips
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={coachFilters}
            onChange={handleCoachFiltersChange}
            options={coachOptions}
            placeholder="Professora"
            searchPlaceholder="Pesquisar"
            disabled={loadingCoachOptions}
            noResultsText="Nenhuma professora encontrada"
            clearButtonLabel="Limpar filtro"
            hideChips
            showSelectionCount={false}
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            placeholder="Tag"
            searchPlaceholder="Pesquisar"
            noResultsText="Nenhuma tag encontrada"
            clearButtonLabel="Limpar tags"
            hideChips
            showSelectionCount={false}
            dropdownClassName={styles['multi-select-dropdown']}
            groups={[
              ...(projectFilterOptions.length > 0
                ? [{
                    title: 'Projeto',
                    values: projectCodeFilters,
                    onChange: handleProjectFiltersChange,
                    options: projectFilterOptions,
                  }]
                : []),
              {
                title: 'Jornada',
                values: journeyFilters,
                onChange: handleClassificationFiltersChange,
                options: STUDENT_JOURNEY_FILTER_OPTIONS,
              },
            ]}
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={workoutStatusFilters}
            onChange={handleWorkoutStatusFiltersChange}
            options={workoutStatusOptions}
            placeholder="Status"
            clearButtonLabel="Limpar filtro"
            hideChips
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            placeholder="FUP"
            clearButtonLabel="Limpar filtro"
            hideChips
            showSelectionCount={false}
            dropdownClassName={styles['multi-select-dropdown']}
            groups={[
              {
                title: 'Status',
                values: fupStatus,
                onChange: (values) => handleFupStatusFiltersChange(values as OrchestratorFupStatus[]),
                options: fupStatusOptions,
              },
              {
                title: 'Data',
                values: fupDateFilter,
                onChange: handleFupDateFilterChange,
                options: fupDateFilterOptions,
              },
            ]}
          />
        </FilterGroup>
        <FilterGroup>
          <DatePresetFilter
            dateStart={dateStart}
            dateEnd={dateEnd}
            onChange={handleDateChange}
            placeholder="Data de pagamento"
            presetOptions={ORCHESTRATOR_PAYMENT_DATE_PRESETS}
            allowEmpty
          />
        </FilterGroup>
        <FilterGroup>
          <DatePresetFilter
            dateStart={dueDateStart}
            dateEnd={dueDateEnd}
            onChange={handleDueDateChange}
            placeholder="Data de vencimento"
            presetOptions={ORCHESTRATOR_PAYMENT_DATE_PRESETS}
            allowEmpty
          />
        </FilterGroup>
        <FilterGroup>
          <DatePresetFilter
            dateStart={lastWorkoutDateStart}
            dateEnd={lastWorkoutDateEnd}
            onChange={handleLastWorkoutDateChange}
            placeholder="Treino anterior"
            presetOptions={ORCHESTRATOR_LAST_WORKOUT_DATE_PRESETS}
            allowEmpty
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={handleClearFilters} />
        )}
      </FilterBar>

      <FilterChipsBar chips={unifiedChips} />

      <PageBody>
        {loading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <EmptyState message="Nenhum ciclo encontrado com os filtros selecionados." />
        ) : (
          <DataTable
            className={styles['orc-table']}
            subgrid
            headers={[
              <span
                key="nome"
                data-dt-col="1"
                data-header-label="Nome"
                className={styles['sortable-header']}
                onClick={() => setFilter(
                  'sortOrder',
                  (currentSortOrder) => (currentSortOrder === 'asc' ? 'desc' : 'asc'),
                )}
              >
                Nome
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                  {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                </span>
              </span>,
              <span key="anotacoes" data-dt-col="2">Anotações</span>,
              <span key="pagamento" data-dt-col="3">Pagamento</span>,
              <span key="ultimo-treino" data-dt-col="4">Último treino</span>,
              <span key="fup" data-dt-col="5">FUP</span>,
              <span key="data-prevista" data-dt-col="6">Data prevista</span>,
              <span key="observacoes" data-dt-col="7" className={styles['orc-header-notes-label']}>Observações</span>,
              <span key="status" data-dt-col="8">Status</span>,
              <span key="acoes" data-dt-col="9" className={dataTableStyles['actions-header']}>Ações</span>,
            ]}
            highlightTerm={searchName}
          >
            {rows.map((row) => {
              const displayedCycleStatus = getDisplayedCycleStatus(row);
              const canSchedule = displayedCycleStatus === 'ready_for_workout';
              const canOpenCalendar = canSchedule;
              const isChanged = changedIds.has(row.student_id);
              const isSaving = savingIds.has(row.student_id);

              return (
                <DataTable.Row
                  key={row.id}
                  className={styles['orc-row']}
                  sensitive={Boolean(row.is_sensitive)}
                >
                  <div
                    className={`${styles['col-name']} ${styles['orc-card-intro']}`}
                    data-dt-col="1"
                    data-card-label=""
                  >
                    <div className={styles['name-field']} data-mobile-label="Nome">
                      <div className={styles['student-name-row']}>
                        <span className={styles['student-name']} onClick={() => navigate(`/alunas/${row.student_id}/editar`)} style={{ cursor: 'pointer' }}>
                          {row.student_name}
                        </span>
                        {row.pending_performance_delivery_days != null && row.pending_performance_delivery_days > 0 && (
                          <Tooltip
                            className={styles['pending-performance-tooltip']}
                            placement="bottom"
                            content={`Esta aluna tem um treino enviado para Performance há mais de ${row.pending_performance_delivery_days} dias que ainda não foi marcado como entregue.`}
                          >
                            <button
                              type="button"
                              className={styles['pending-performance-warning']}
                              aria-label={`Treino pendente na Performance há mais de ${row.pending_performance_delivery_days} dias`}
                            >
                              <span className={styles['warning-icon']} aria-hidden="true">
                                warning
                              </span>
                            </button>
                          </Tooltip>
                        )}
                        <CopyStudentNameButton studentName={row.student_name} />
                      </div>
                    </div>
                    <div className={styles['name-field']} data-mobile-label="E-mail">
                      {row.student_email ? (
                        <span className={styles['student-email']}>{row.student_email}</span>
                      ) : (
                        <span className={styles['email-empty']}>E-mail não informado</span>
                      )}
                    </div>
                    <div className={styles['name-field']} data-mobile-label="Professora">
                      <div className={styles['coach-row']}>
                        <span className={styles['coach-label']}>Professora:</span>
                        {row.coach_name ? (
                          <span className={styles['coach-name']}>{row.coach_name}</span>
                        ) : (
                          <span className={styles['coach-empty']}>Sem professora</span>
                        )}
                        {canChangeCoach && (
                          <Tooltip content="Alterar professora">
                            <button
                              type="button"
                              className={styles['btn-edit-coach']}
                              aria-label="Alterar professora"
                              onClick={() => setPendingCoachEdit({
                                studentId: row.student_id,
                                studentName: row.student_name,
                                coachId: row.coach_id ? String(row.coach_id) : '',
                              })}
                            >
                              <Icon name="change_circle" size={16} />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    {(row.classification?.status === StudentClassificationStatus.NEW_STUDENT || row.project || row.is_reactivated || row.is_sensitive || row.is_pregnant || row.is_red_flag) && (
                      <div className={styles['student-badges']}>
                        <NewStudentBadge row={row} />
                        <ProjectBadge project={row.project} />
                        <ReactivatedBadge visible={row.is_reactivated} />
                        <SensitiveStudentBadge visible={row.is_sensitive} reason={row.sensitive_reason} />
                        <PregnantStudentBadge visible={row.is_pregnant} pregnantUntil={row.pregnant_until} />
                        <RedFlagBadge visible={row.is_red_flag} />
                      </div>
                    )}
                  </div>

                  <div
                    className={annotationStyles.colAnnotations}
                    data-dt-col="2"
                    data-card-label=""
                  >
                    <OrchestratorMobileSectionLabel>Anotações</OrchestratorMobileSectionLabel>
                    <div className={`${styles['orc-mobile-section-body']} ${annotationStyles.annotationsBody}`}>
                      {row.notes?.trim() ? (
                        <p className={annotationStyles.annotationsPreview} title={row.notes}>
                          {row.notes}
                        </p>
                      ) : (
                        <span className={annotationStyles.annotationsEmpty}>Sem anotações</span>
                      )}
                      <ViewLinkButton
                        label="Editar"
                        icon={null}
                        onClick={() => {
                          setPendingAssignmentNotesEdit({
                            studentId: row.student_id,
                            studentName: row.student_name,
                            assignmentId: row.workout_assignment_id,
                            competence: row.competence,
                            notes: row.notes ?? '',
                          });
                        }}
                      />
                    </div>
                  </div>

                  <PaymentCell row={row} />

                  <div
                    className={styles['col-last-workout']}
                    data-dt-col="4"
                    data-card-label=""
                  >
                    <OrchestratorMobileSectionLabel>Último treino</OrchestratorMobileSectionLabel>
                    <div className={styles['orc-mobile-section-body']}>
                    {!row.last_workout_date ? (
                      <span className={styles['last-workout-empty']}>Sem treino anterior</span>
                    ) : (
                      <span className={styles['last-workout-text']}>{formatDate(row.last_workout_date)}</span>
                    )}
                    </div>
                  </div>

                  <FupCell
                    row={row}
                    onViewResponse={openFupViewModal}
                    onLinkCopied={handleFupLinkCopied}
                    onRemoveLinkRequest={handleFupLinkRemoveRequest}
                  />

                  <div className={styles['col-date']} data-dt-col="6" data-card-label="">
                    <OrchestratorMobileSectionLabel>Data prevista</OrchestratorMobileSectionLabel>
                    <div className={styles['orc-mobile-section-body']}>
                    {canSchedule && (
                      <Tooltip content="Definir data prevista do treino">
                        <span
                          className={`${styles['btn-calendar-wrapper']} ${!canOpenCalendar ? styles['btn-calendar-wrapper-disabled'] : ''}`}
                        >
                          <button
                            type="button"
                            className={styles['btn-calendar']}
                            onClick={() => {
                              if (!canOpenCalendar) return;
                              setModalRowId(row.student_id);
                            }}
                            disabled={!canOpenCalendar}
                            aria-label="Definir data prevista do treino"
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>calendar_month</span>
                          </button>
                        </span>
                      </Tooltip>
                    )}
                    <div className={styles['date-value']}>
                      {row.expected_delivery_date ? (
                        <span className={styles['date-text']}>{formatDate(row.expected_delivery_date)}</span>
                      ) : (
                        <span className={styles['date-empty']}>Sem data</span>
                      )}
                      {scheduleDateError?.studentId === row.student_id ? (
                        <span className={styles['date-error']}>{scheduleDateError.message}</span>
                      ) : null}
                    </div>
                    </div>
                  </div>

                  <div className={styles['col-notes']} data-dt-col="7">
                    <ViewLinkButton
                      label="Registrar"
                      icon="add_notes"
                      onClick={() => {
                        void openStudentNotesModal({
                          studentId: row.student_id,
                          studentName: row.student_name,
                        });
                      }}
                    />
                  </div>

                  <div className={styles['col-status']} data-dt-col="8" data-card-label="">
                    <OrchestratorMobileSectionLabel>Status</OrchestratorMobileSectionLabel>
                    <div className={styles['orc-mobile-section-body']}>
                    <Badge variant={STATUS_BADGE_VARIANT[displayedCycleStatus]} solid>
                      {WORKOUT_STATUS_LABELS[displayedCycleStatus]}
                    </Badge>
                    </div>
                  </div>

                  <div className={`${styles['col-actions']} ${styles['orc-card-menu']}`} data-dt-col="9">
                    {canSchedule && (
                      <Tooltip
                        content={
                          row.expected_delivery_date
                            ? null
                            : 'Informe a data prevista do treino para prosseguir'
                        }
                      >
                        <span className={styles['btn-calendar-wrapper']} style={{ marginRight: -4 }}>
                          <Button
                            variant="primary"
                            size="xs"
                            className={`${styles['btn-save']} ${isChanged ? styles['btn-save-active'] : ''}`}
                            disabled={!isChanged || isSaving || !canOpenCalendar || !row.expected_delivery_date}
                            onClick={() => {
                              if (!row.expected_delivery_date) return;
                              setScheduleDateError(undefined);
                              setPendingScheduleSave({
                                workoutAssignmentId: row.workout_assignment_id ?? null,
                                studentId: row.student_id,
                                studentName: row.student_name,
                                expectedDeliveryDate: row.expected_delivery_date,
                              });
                            }}
                          >
                            {isSaving ? 'Salvando…' : 'Salvar'}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    <KebabMenu
                      items={[
                        {
                          icon: 'add_notes',
                          label: 'Registrar Observações',
                          onClick: () => {
                            void openStudentNotesModal({
                              studentId: row.student_id,
                              studentName: row.student_name,
                            });
                          },
                        },
                        ...(canOpenCalendar ? [{
                          label: 'Data do Treino',
                          onClick: () => setModalRowId(row.student_id),
                        }] : []),
                        { label: 'Ver aluna', onClick: () => navigate(`/alunas/${row.student_id}/editar`) },
                      ]}
                    />
                  </div>
                </DataTable.Row>
              );
            })}
          </DataTable>
        )}
      </PageBody>

      {pagination.totalPages > 0 && (
        <div className={styles['bottom-row']}>
          <PageSizeSelect
            value={pageSize}
            options={pageSizeOptions}
            onChange={handlePageSizeChange}
          />
          <div className={styles['pagination-center']}>
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      {modalRow && (
        <CalendarModal
          studentName={modalRow.student_name}
          lastWorkoutDate={modalRow.last_workout_date}
          onConfirm={(date) => {
            setExpectedDeliveryDate(modalRow.student_id, date);
            setModalRowId(null);
          }}
          onCancel={() => setModalRowId(null)}
        />
      )}

      <ConfirmModal
        open={Boolean(pendingScheduleSave)}
        title="Confirmar envio para Performance"
        message={pendingScheduleSave
          ? `O treino irá para a tela de Performance com data prevista de entrega para ${formatDate(pendingScheduleSave.expectedDeliveryDate)}.`
          : ''}
        confirmLabel="Confirmar"
        cancelLabel="Cancelar"
        variant="info"
        loading={Boolean(
          pendingScheduleSave
          && savingIds.has(pendingScheduleSave.studentId)
        )}
        onCancel={() => {
          setPendingScheduleSave(null);
        }}
        onConfirm={handleScheduleSaveConfirm}
      />

      <ManagedFupResponseModal
        fup={pendingFupView ? {
          studentId: pendingFupView.studentId,
          studentFupId: pendingFupView.studentFupId,
          assignmentId: pendingFupView.assignmentId,
          studentName: pendingFupView.studentName,
          createdAt: pendingFupView.fup_created_at,
        } : null}
        canManage
        onClose={closeFupView}
        onSaved={handleFupSaved}
        onDeleted={handleFupDeleted}
      />

      <NewStudentAnswersModal
        open={Boolean(pendingNewStudentAnswers)}
        studentId={pendingNewStudentAnswers?.studentId ?? null}
        studentName={pendingNewStudentAnswers?.studentName}
        onClose={() => setPendingNewStudentAnswers(null)}
      />

      <OrchestratorAssignmentNotesModal
        open={Boolean(pendingAssignmentNotesEdit)}
        loading={savingAssignmentNotes}
        studentName={pendingAssignmentNotesEdit?.studentName ?? ''}
        initialNotes={pendingAssignmentNotesEdit?.notes ?? ''}
        onCancel={() => {
          if (!savingAssignmentNotes) {
            setPendingAssignmentNotesEdit(null);
          }
        }}
        onSave={(notes) => {
          void handleAssignmentNotesSave(notes);
        }}
      />

      <StudentNotesModal
        open={Boolean(pendingNotesSave)}
        studentId={pendingNotesSave?.studentId}
        studentName={pendingNotesSave?.studentName ?? ''}
        notes={pendingNotesSave?.history ?? []}
        summary={pendingNotesSave?.summary}
        value={pendingNotesSave?.newNote ?? ''}
        loading={submittingNotes || loadingNotesHistory}
        canWriteObservations={canWriteObservations}
        onChange={(nextValue) => {
          setPendingNotesSave((current) => current ? { ...current, newNote: nextValue } : current);
        }}
        onSummaryGenerated={(summary) => {
          setPendingNotesSave((current) => current ? { ...current, summary } : current);
        }}
        onCancel={() => setPendingNotesSave(null)}
        onSubmit={handleNotesSaveConfirm}
        onDeleteNote={handleDeleteNote}
      />

      <ConfirmModal
        open={Boolean(pendingCoachEdit)}
        title={pendingCoachEdit ? `Professora — ${pendingCoachEdit.studentName}` : 'Professora'}
        message={pendingCoachEdit ? 'Selecione a professora responsável por esta aluna.' : ''}
        confirmLabel="Salvar"
        cancelLabel="Cancelar"
        variant="info"
        loading={submittingCoachEdit}
        onCancel={() => setPendingCoachEdit(null)}
        onConfirm={handleCoachEditConfirm}
      >
        <div className={styles['coach-modal-fields']}>
          <SearchableSelect
            value={pendingCoachEdit?.coachId ?? ''}
            onChange={(value) => {
              setPendingCoachEdit((current) => current ? { ...current, coachId: value } : current);
            }}
            options={coachSelectOptions}
            placeholder={loadingCoachOptions ? 'Carregando professoras...' : 'Selecione a professora'}
            searchPlaceholder="Pesquisar"
            noResultsText="Nenhuma professora encontrada"
            disabled={submittingCoachEdit || loadingCoachOptions}
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(pendingFupLinkRemoval)}
        title="Remover link de FUP"
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        variant="danger"
        loading={submittingFupLinkRemoval}
        onCancel={() => setPendingFupLinkRemoval(null)}
        onConfirm={handleFupLinkRemoveConfirm}
      >
        <p className={styles['fup-remove-modal-message']}>
          Deseja realmente remover este link de FUP?
        </p>
        <p className={styles['fup-remove-modal-message']}>
          Esta ação não pode ser desfeita.
        </p>
        <p className={styles['fup-remove-modal-message']}>
          Se precisar compartilhar novamente, será necessário gerar um novo link.
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(pendingDeliver)}
        title="Entregar treino"
        message={pendingDeliver ? `Confirme a entrega do treino para ${pendingDeliver.studentName}.` : ''}
        confirmLabel="Confirmar entrega"
        cancelLabel="Cancelar"
        variant="success"
        loading={submittingDeliver}
        onCancel={() => {
          setDeliverDateError(undefined);
          setPendingDeliver(null);
        }}
        onConfirm={handleDeliverConfirm}
      >
        <FormInput
          id="orchestrator-deliver-date"
          label="Data da entrega"
          type="date"
          value={pendingDeliver?.deliveredDate ?? ''}
          onChange={(event) => {
            const nextDate = event.target.value;
            setDeliverDateError(undefined);
            setPendingDeliver((current) => current ? { ...current, deliveredDate: nextDate } : current);
          }}
          error={deliverDateError}
          required
          disabled={submittingDeliver}
        />
      </ConfirmModal>
    </PageContainer>
  );
};

export default Orchestrator;
